import React, {useCallback, useEffect, useState} from 'react';
import {Box, Button, Tooltip, Typography} from '@mui/material';
import {setNestedObjectValues} from 'formik';
import {useSnackbar} from 'notistack';
import {useSaveBuilder} from '../../useSaveBuilder/useSaveBuilder';
import {useAccountContext} from '../../../../contexts/AccountContext';
import {builderFieldsType} from '../builderFields';
import {
  finaliseBuilderFactory,
  saveDraftBuilderFactory,
  setContactnameFromGoals,
} from './builderUtils';
import UnfinishedFormPrompt from '../../../../components/Common/UnfinishedFormPrompt';
import {useBuilderContext} from '../useBuilderContext';
import {FixedFooter} from '../../../../components/Layout/Layout';
import {OvbuilderDetails_ovrecommendDetails as OvbuilderDetails_ovbuilderDetails} from '../../../../schema-types/OvbuilderDetails';
import {
  OVRecommendActionEnum,
  GlobalPermissionEnum,
} from '../../../../schema-types/globalTypes';
import {SPP_BUILDER_TYPES} from '../../constants/SolutionType';
import {
  useAutoForm,
  useLoadedAutoField,
} from '../../../../components/AutoForm/useAutoForm';
import {ScrollableModal} from '../../../../components/Common/ScrollableModal';
import {useDjangoContext} from '../../../../contexts/useAuthContext';
import {useBuilderPotsQuery} from '../../hooks/useBuilderPotsQuery';

export const reccNonEditable = (
  builderDetails: OvbuilderDetails_ovbuilderDetails | null
) => {
  return builderDetails?.recommends?.some((x) => !!x.curoId);
};

const getReccWarning = (
  values: builderFieldsType
): {error: string | null; allowToSave: boolean} => {
  if (values.mainPlanType === 'pension' || values.forcedMainPlanType === 'pension') {
    if (values.sppType === SPP_BUILDER_TYPES.ExecutionOnly) {
      return {
        error: `The Saltus SIPP is currently an advised product only. You have selected execution only.`,
        allowToSave: false,
      };
    }
  }
  if (
    values.planAction === OVRecommendActionEnum.TRANSFER &&
    (values.mainPlanType === 'pension' || values.forcedMainPlanType === 'pension')
  ) {
    return {
      error: `Don't forget to add instructions for any existing regular withdrawals/contributions that the client wishes to continue following the transfer`,
      allowToSave: true,
    };
  }
  return {error: null, allowToSave: true};
};

export const CreateBuilderFooter: React.FC = () => {
  const {sourcePots, destinationPots} = useBuilderPotsQuery();
  const {perms} = useDjangoContext();
  const {values, dirty, validateForm, isValid, setTouched, touched} =
    useAutoForm<builderFieldsType>();
  const [saving, setSaving] = useState(false);

  const [warning, setWarning] = useState<{error: string | null; allowToSave: boolean}>({
    allowToSave: true,
    error: null,
  });
  const [contactName, setContactName] = useState<string>('');
  // const {accountContactsLoading, accountContactsData, accountContactsError} =
  //   useAccountContacts();
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {account, accountId, accountLoading, potsLoading} = useAccountContext();
  const {builderDetails, builderReviewId, loadingOvRecommend} = useBuilderContext();
  const {createUpdate} = useSaveBuilder(setSaving, values.how);
  const saveDraftBuilder = saveDraftBuilderFactory({
    setSaving,
    createUpdate,
    values,
    contactName,
    sources: sourcePots!,
    destinations: destinationPots,
  });
  const closeWarning = useCallback(
    () => setWarning({allowToSave: true, error: null}),
    []
  );
  const finaliseBuilder = finaliseBuilderFactory({
    createUpdate,
    closeWarning,
    values,
    contactName,
    sources: sourcePots!,
    destinations: destinationPots,
    setSaving,
    closeSnackbar,
    validateForm,
    setNestedObjectValues,
    setTouched,
    enqueueSnackbar,
  });

  useEffect(() => {
    if (account) {
      setContactnameFromGoals({
        values,
        account,
        setContactName,
      });
    }
  }, [account, values.contact]);

  const checkWarningAndFinalize = (skipChecks: boolean) => {
    if (skipChecks && perms.includes(GlobalPermissionEnum.can_access_admin_features)) {
      return finaliseBuilder();
    }
    const newWarning = getReccWarning(values);
    if (newWarning.error) {
      setWarning(newWarning);
    } else {
      finaliseBuilder();
    }
  };
  const disableSaving = reccNonEditable(builderDetails);
  const submitFirstWord = builderDetails?.id ? 'Update' : 'Add';
  const finaliseButtonText = `${submitFirstWord} ${values.how}`;

  const getDisabledReason = (): string => {
    if (saving) return 'Saving in progress';
    if (potsLoading) return 'Loading pots...';
    if (accountLoading) return 'Loading account...';
    if (loadingOvRecommend) return 'Loading recommendation...';
    if (reccNonEditable(builderDetails))
      return 'This recommendation has already been submitted to Curo and cannot be edited';
    return '';
  };
  const disabledReason = getDisabledReason();
  const isDisabled = !!disabledReason;

  return (
    <>
      <ScrollableModal
        className="modal"
        open={!!warning.error}
        onClose={() => closeWarning()}
      >
        <Box height="100%" display="flex" alignItems="center" justifyContent="center">
          <Box p={4} borderRadius="8px" bgcolor="white">
            <Typography variant="h6">{warning.error}</Typography>
            <Box mt={2} />
            <Box display="flex" justifyContent="space-between">
              <Button color="primary" variant="contained" onClick={() => closeWarning()}>
                Close popup
              </Button>
              <Box />
              {warning.allowToSave ? (
                <Button
                  data-test="Finalise"
                  color="primary"
                  variant="contained"
                  onClick={finaliseBuilder}
                >
                  Continue
                </Button>
              ) : null}
            </Box>
          </Box>
        </Box>
      </ScrollableModal>
      <ScrollableModal size="small" className="modal" open={saving}>
        <Box
          flex={1}
          className="saving"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Typography fontSize={16}>
            {saving ? `Saving ${values.how}...` : null}
          </Typography>
        </Box>
      </ScrollableModal>
      <Box width="100%" pb={12} />
      <FixedFooter>
        {disableSaving && builderDetails?.review?.dateSubmitted ? (
          <Box mr={1} display="flex" justifyContent="flex-end">
            <Typography color="primary">Already submitted, cannot re-submit</Typography>
            <Box />
          </Box>
        ) : null}
        <Box mt={1} display="flex" justifyContent="flex-end">
          <Box mr={1}>
            <UnfinishedFormPrompt
              ariaLabel="Requirements"
              path="../requirements-builder"
              dirty={dirty}
            />
          </Box>
          {values.planAction && (
            <>
              <Box mr={1} display="flex" gap={2} alignContent="center">
                <Tooltip title={disabledReason}>
                  <span>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={isDisabled}
                      onClick={saveDraftBuilder}
                    >
                      {saving ? 'Saving...' : 'Save As Draft'}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              <Box>
                <Tooltip title={disabledReason}>
                  <span>
                    <Button
                      data-test="Finalise"
                      variant="contained"
                      color="primary"
                      disabled={isDisabled}
                      onClick={(e) => checkWarningAndFinalize(e.altKey)}
                    >
                      {saving ? 'Saving...' : finaliseButtonText}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </>
          )}
        </Box>
      </FixedFooter>
    </>
  );
};
